import { useAuth } from "@clerk/clerk-react";
import axios from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api_endpoint } from "@/constants/constant";

const flattenDeep = (value) => {
  const out = [];
  const stack = [value];
  while (stack.length) {
    const next = stack.pop();
    if (Array.isArray(next)) {
      for (let i = next.length - 1; i >= 0; i -= 1) stack.push(next[i]);
    } else if (next != null) {
      out.push(next);
    }
  }
  return out;
};

const parseDateToMs = (value) => {
  const d = value ? new Date(value) : null;
  const t = d && !Number.isNaN(d.getTime()) ? d.getTime() : 0;
  return t;
};

export default function useProjectAiReview({
  projectId,
  studentId,
  enabled = false,
} = {}) {
  const { getToken } = useAuth();

  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const lastKeyRef = useRef(null);

  const fetchReviews = useCallback(async () => {
    const token = await getToken();
    if (!token) throw new Error("No auth token");

    const reqConfig = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };

    const res = await axios.post(
      api_endpoint + "/project/get/reviews",
      {
        project_id: projectId,
        student_id: studentId,
      },
      reqConfig
    );

    return res.data;
  }, [getToken, projectId, studentId]);

  const reload = useCallback(
    async ({ force = false } = {}) => {
      if (!projectId || !studentId) return;

      const key = `${projectId}::${studentId}`;
      if (!force && lastKeyRef.current === key && payload) return;

      setLoading(true);
      try {
        const data = await fetchReviews();
        lastKeyRef.current = key;
        setPayload(data);
      } catch (err) {
        console.error(
          "[project/get/reviews] Failed:",
          err?.response?.status,
          err?.response?.data ?? err
        );
      } finally {
        setLoading(false);
      }
    },
    [fetchReviews, payload, projectId, studentId]
  );

  useEffect(() => {
    if (!enabled) return;
    reload();
  }, [enabled, reload]);

  const aiReviews = useMemo(() => {
    const raw = Array.isArray(payload)
      ? payload
      : payload?.reviews?.ai_reviews ??
        payload?.reviews?.ai_review ??
        payload?.reviews?.aiReviews ??
        payload?.reviews?.aiReview ??
        payload?.reviews ??
        payload?.ai_reviews ??
        payload?.ai_review ??
        payload?.aiReviews ??
        payload?.aiReview ??
        [];

    const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
    const flattened = flattenDeep(list);

    return flattened.filter((item) => {
      if (!item || typeof item !== "object") return false;
      const feedbackStr = item?.feedback;
      if (typeof feedbackStr !== "string" || !feedbackStr.trim()) return false;
      const type = String(item?.review_type ?? item?.type ?? "").toUpperCase();
      return type ? type === "AI" : true;
    });
  }, [payload]);

  const latestAIReview = useMemo(() => {
    if (!Array.isArray(aiReviews) || aiReviews.length === 0) return null;

    const sorted = [...aiReviews].sort((a, b) => {
      const at = parseDateToMs(a?.reviewed_at ?? a?.created_at ?? a?.updated_at);
      const bt = parseDateToMs(b?.reviewed_at ?? b?.created_at ?? b?.updated_at);
      return bt - at;
    });

    return sorted[0] ?? null;
  }, [aiReviews]);

  const aiReviewText = useMemo(() => {
    const item = latestAIReview;
    if (!item) return "";
    return typeof item?.feedback === "string" ? item.feedback : "";
  }, [latestAIReview]);

  const hasAIReview = Boolean(aiReviewText && aiReviewText.trim().length > 0);

  const rejectLatest = useCallback(async () => {
    const reviewId = latestAIReview?.id;
    if (!reviewId) return false;

    try {
      setDeleting(true);
      const token = await getToken();
      if (!token) throw new Error("No auth token");

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      await axios.delete(api_endpoint + `/project/auto-review/${reviewId}`, reqConfig);

      toast.success("AI review rejected");

      lastKeyRef.current = null;
      await reload({ force: true });
      return true;
    } catch (err) {
      console.error(
        "[project/auto-review/:id] Delete failed:",
        err?.response?.status,
        err?.response?.data ?? err
      );
      toast.error("Failed to reject AI review");
      return false;
    } finally {
      setDeleting(false);
    }
  }, [getToken, latestAIReview?.id, reload]);

  return {
    payload,
    loading,
    deleting,
    reload,
    aiReviews,
    latestAIReview,
    aiReviewText,
    hasAIReview,
    rejectLatest,
  };
}
